/**
 * Turkish and English for the dashboard. The English text is the key, so a template reads
 * `$t('Programs')`; a string without a Turkish entry shows as written. Messages that come from
 * the API are English too and are looked up the same way.
 */
import { ref } from 'vue'

const KEY = 'ssiq-lang'

const initial = () => {
  try {
    const saved = localStorage.getItem(KEY)
    if (saved === 'tr' || saved === 'en') return saved
  } catch (err) { /* private window */ }
  return (navigator.language || 'en').toLowerCase().startsWith('tr') ? 'tr' : 'en'
}

export const lang = ref(initial())
document.documentElement.lang = lang.value

export const setLang = (value) => {
  lang.value = value === 'tr' ? 'tr' : 'en'
  document.documentElement.lang = lang.value
  try { localStorage.setItem(KEY, lang.value) } catch (err) { /* ignore */ }
}

const TR = {
  // navigation
  'Dashboard': 'Panel',
  'Plans': 'Planlar',
  'Logout': 'Çıkış',
  'Toggle theme': 'Temayı değiştir',
  'Language': 'Dil',

  // login
  'Geotechnical Analysis Suite': 'Geoteknik Analiz Paketi',
  'Login': 'Giriş',
  'Sign Up': 'Kayıt ol',
  'Email': 'E-posta',
  'Password': 'Şifre',
  'Loading...': 'Yükleniyor...',
  'Create Account': 'Hesap oluştur',
  'Forgot your password?': 'Şifrenizi mi unuttunuz?',
  'tier:': 'planı:',
  'Basic programs, saving, reports, export': 'Temel programlar, kaydetme, raporlar, dışa aktarma',
  'More programs: Plus, Pro, Pro Plus': 'Daha fazla program: Plus, Pro, Pro Plus',
  'Something went wrong': 'Bir sorun oluştu',
  'Signup failed': 'Kayıt başarısız',
  'Login failed': 'Giriş başarısız',
  'Failed to fetch user': 'Kullanıcı bilgisi alınamadı',
  'Invalid email or password': 'E-posta veya şifre hatalı',
  'Email already registered': 'Bu e-posta zaten kayıtlı',
  'User account is inactive': 'Hesap devre dışı',
  'Not authenticated': 'Oturum açılmamış',
  'Invalid token': 'Geçersiz oturum',
  'Could not reach the server.': 'Sunucuya ulaşılamadı.',

  // dashboard
  'Programs': 'Programlar',
  'Pro plan: the Plus programs and Pile Capacity and Pile Wall Analysis are open.': 'Pro plan: Plus programları ile Kazık Taşıma Gücü ve Kazıklı Perde Analizi açık.',
  'Free plan: Limit Equilibrium, Bearing Capacity, Settlement and Soil Correlations. Every program has saving, reports and exports.': 'Ücretsiz plan: Limit Denge, Taşıma Gücü, Oturma ve Zemin Korelasyonları. Her programda kaydetme, rapor ve dışa aktarma var.',
  'Manage plan': 'Planı yönet',
  'See plans': 'Planları gör',
  'Confirm your e-mail.': 'E-postanızı doğrulayın.',
  'We sent a link to': 'Şu adrese bir bağlantı gönderdik:',
  'Until you open it the programs and upgrades stay locked.': 'Bağlantıyı açana kadar programlar ve plan yükseltme kilitli kalır.',
  'Sending...': 'Gönderiliyor...',
  'Send it again': 'Yeniden gönder',
  'I have confirmed': 'Doğruladım',
  'Sent. Check your inbox and the spam folder.': 'Gönderildi. Gelen kutunuza ve spam klasörüne bakın.',
  'Already confirmed.': 'Zaten doğrulanmış.',
  'Could not send the e-mail.': 'E-posta gönderilemedi.',
  'Not confirmed yet.': 'Henüz doğrulanmamış.',
  'Could not check. Try again.': 'Kontrol edilemedi. Tekrar deneyin.',
  'Opening...': 'Açılıyor...',
  'New project': 'Yeni proje',
  'Coming soon': 'Yakında',
  'Tutorial': 'Eğitim',
  'Tutorial:': 'Eğitim:',
  'Close': 'Kapat',
  'Tip.': 'İpucu.',
  'Open with the example': 'Örnekle aç',
  'Full documentation': 'Tüm dokümantasyon',
  'Theory': 'Teori',
  'Could not open the program': 'Program açılamadı',
  'Confirm your e-mail address first: we sent you a link.': 'Önce e-posta adresinizi doğrulayın: size bir bağlantı gönderdik.',
  'E-mail is not set up yet': 'E-posta henüz kurulmadı',

  // groups and programs
  'Slopes': 'Şevler',
  'Rock': 'Kaya',
  'Foundations and ground': 'Temeller ve zemin',
  'Retaining structures': 'İstinat yapıları',
  'Limit Equilibrium': 'Limit Denge',
  '2D Finite Elements': '2B Sonlu Elemanlar',
  'Rock Kinematics': 'Kaya Kinematiği',
  'Rock Mass Strength': 'Kaya Kütlesi Dayanımı',
  'Bearing Capacity': 'Taşıma Gücü',
  'Soil Parameter Correlations': 'Zemin Parametre Korelasyonları',
  'Liquefaction': 'Sıvılaşma',
  'Settlement': 'Oturma',
  'Pile Capacity': 'Kazık Taşıma Gücü',
  'Pile Wall Analysis': 'Kazıklı Perde Analizi',
  'MSE Walls': 'MSE Duvarları (Donatılı Zemin)',
  'Retaining Walls': 'İstinat Duvarları',
  'Slope stability by Bishop, Janbu, Spencer and Morgenstern–Price with automatic critical-surface search.':
    'Bishop, Janbu, Spencer ve Morgenstern–Price yöntemleriyle şev stabilitesi; kritik yüzey otomatik aranır.',
  'Slopes, embankments and excavations by strength reduction.': 'Dayanım azaltma yöntemiyle şev, dolgu ve kazı analizi.',
  'Stereonet kinematic screening, wedge, planar and toppling failure, bolt design.':
    'Stereonet ile kinematik tarama; kama, düzlemsel ve devrilme kırılması; kaya bulonu tasarımı.',
  'Generalised Hoek–Brown strength and modulus of the rock mass.': 'Genelleştirilmiş Hoek–Brown ile kaya kütlesi dayanımı ve deformasyon modülü.',
  'Shallow foundations by every classic method and EN 1997-1.': 'Sığ temeller: tüm klasik yöntemler ve EN 1997-1.',
  'Soil parameters from SPT, CPT and laboratory tests with the scatter of each correlation; hands off to Settle and Bearing.':
    'SPT, CPT ve laboratuvar deneylerinden zemin parametreleri; her korelasyonun saçılımıyla birlikte; sonuçlar Oturma ve Taşıma Gücü programlarına aktarılır.',
  'SPT and CPT triggering (Idriss & Boulanger, NCEER, Cetin, TBDY 2018), settlement, lateral spreading, LPI and LSN.':
    'SPT ve CPT ile sıvılaşma tetiklenmesi (Idriss & Boulanger, NCEER, Cetin, TBDY 2018), oturma, yanal yayılma, LPI ve LSN.',
  'Immediate, consolidation and secondary settlement with time curves.': 'Anlık, konsolidasyon ve ikincil oturma; zaman–oturma eğrileri.',
  'Axial capacity and settlement of single piles, groups and rock sockets.': 'Tekil kazık, kazık grubu ve kaya soketinin eksenel taşıma gücü ve oturması.',
  'Steel sheet piles or reinforced concrete bored piles: free-earth support and Winkler beam-spring analysis with stages.':
    'Çelik palplanş veya betonarme fore kazık perde: serbest zemin desteği ve aşamalı Winkler yay-kiriş analizi.',
  'Mechanically stabilised earth walls: external and internal stability.': 'Mekanik olarak stabilize zemin (MSE) duvarları: dış ve iç stabilite.',
  'Cantilever RC retaining walls: stability, TBDY 2018 and TS 500 design.': 'Konsol betonarme istinat duvarları: stabilite, TBDY 2018 ve TS 500 ile tasarım.',

  // pricing
  'Every plan has all the features of a program: saving, reports, exports. The plans differ by the programs they open.': 'Her planda bir programın tüm özellikleri var: kaydetme, rapor, dışa aktarma. Planlar açtıkları programlarla ayrışır.',
  'Monthly': 'Aylık',
  'Yearly': 'Yıllık',
  '2 months free': '2 ay bedava',
  "Let's talk": 'Görüşelim',
  'year': 'yıl',
  'month': 'ay',
  'a month, billed yearly': 'aylık, yıllık faturalanır',
  'Free forever, no card': 'Süresiz ücretsiz, kart gerekmez',
  'Per-user pricing, coming soon': 'Kullanıcı başına fiyat, yakında',
  'Cancel any time': 'İstediğiniz zaman iptal edin',
  'Your plan': 'Planınız',
  'Included': 'Dahil',
  'Manage subscription': 'Aboneliği yönet',
  'Included in your plan': 'Planınıza dahil',
  'Upgrade to Pro': "Pro'ya yükselt",
  'Contact us': 'Bize ulaşın',
  'Could not reach the payment service': 'Ödeme servisine ulaşılamadı',
  'Could not load the plans': 'Planlar yüklenemedi',
  'Every program, unlimited analyses': 'Tüm programlar, sınırsız analiz',
  'Results and CSV export': 'Sonuçlar ve CSV dışa aktarma',
  'Hand-off files between programs': 'Programlar arası aktarım dosyaları',
  'Everything in Free': "Ücretsiz'deki her şey",
  'Save project files': 'Proje dosyalarını kaydetme',
  'Saving projects, Excel export, PDF, Word and HTML reports, DXF import and export': 'Proje kaydetme, Excel çıktısı, PDF, Word ve HTML raporlar, DXF içe/dışa aktarma',
  'Reports without the logo': 'Logosuz raporlar',
  'Excel export of studies': 'Parametrik çalışmaların Excel çıktısı',
  'Several users, one invoice': 'Birden çok kullanıcı, tek fatura',
  'Shared projects (planned)': 'Paylaşılan projeler (planlanıyor)',
  'Commercial licence on request': 'İstek üzerine ticari lisans',

  // billing success, verify, forgot, reset
  'You are on Pro': 'Pro plandasınız',
  'Saving and reports are now enabled in every program.': 'Kaydetme ve raporlar artık tüm programlarda açık.',
  'Go to the programs': 'Programlara git',
  'Payment received': 'Ödeme alındı',
  'Your plan is being activated. It can take a minute; reload this page or open the programs.':
    'Planınız etkinleştiriliyor. Bir dakika sürebilir; sayfayı yenileyin veya programları açın.',
  'Activating your plan...': 'Planınız etkinleştiriliyor...',
  'Waiting for the payment service to confirm.': 'Ödeme servisinin onayı bekleniyor.',
  'Confirming your e-mail...': 'E-postanız doğrulanıyor...',
  'E-mail confirmed': 'E-posta doğrulandı',
  'Your account is ready. The programs are open to you now.': 'Hesabınız hazır. Programlar artık açık.',
  'Log in': 'Giriş yap',
  'This link did not work': 'Bu bağlantı çalışmadı',
  'Back to the dashboard': 'Panele dön',
  'Log in to get a new link': 'Yeni bağlantı için giriş yapın',
  'This link is invalid or has expired.': 'Bu bağlantı geçersiz veya süresi dolmuş.',
  'Enter your e-mail address and we will send you a link to choose a new one.':
    'E-posta adresinizi girin, yeni şifre seçmeniz için bir bağlantı gönderelim.',
  'Send the link': 'Bağlantıyı gönder',
  'Back to login': 'Girişe dön',
  'If that address has an account, we have sent a link to it.': 'Bu adresin bir hesabı varsa ona bir bağlantı gönderdik.',
  'Resetting a password by e-mail is not set up yet.': 'E-postayla şifre sıfırlama henüz kurulmadı.',
  'Choose a new password': 'Yeni şifre seçin',
  'New password': 'Yeni şifre',
  'At least 8 characters': 'En az 8 karakter',
  'Repeat it': 'Tekrar girin',
  'Saving...': 'Kaydediliyor...',
  'Save the password': 'Şifreyi kaydet',
  'The two passwords differ.': 'İki şifre birbirinden farklı.',
  'Ask for a new link': 'Yeni bağlantı iste',
  'Password changed': 'Şifre değiştirildi',
  'You can log in with your new password now.': 'Artık yeni şifrenizle giriş yapabilirsiniz.',
  'This link is invalid, has expired or was already used.': 'Bu bağlantı geçersiz, süresi dolmuş veya daha önce kullanılmış.',
  'Free': 'Ücretsiz',
  'Education': 'Eğitim',
  'Plus': 'Plus',
  'Pro Plus': 'Pro Plus',
  'Upgrade to Pro Plus': "Pro Plus'a yükselt",
  'Pro Plus plan: every program is open, the finite element ones included.': 'Pro Plus plan: sonlu eleman programları dahil tüm programlar açık.',
  'Everything in Pro': "Pro'daki her şey",
  'The finite element programs: SlipSurface IQ 2D, 3D and Tunnel': 'Sonlu eleman programları: SlipSurface IQ 2B, 3B ve Tünel',
  '3D Finite Elements': '3B Sonlu Elemanlar',
  'Three-dimensional slopes, excavations and foundations by finite elements.': 'Sonlu elemanlarla üç boyutlu şev, kazı ve temel analizi.',
  'This program is part of the Pro Plus plan.': 'Bu program Pro Plus planına dahildir.',
  'Upgrade to Plus': "Plus'a yükselt",
  'Change plan': 'Planı değiştir',
  'Needs a higher plan': 'Daha üst bir plan gerekir',
  '{program} is part of the {plan} plan.': '{program} {plan} planına dahildir.',
  'Plus plan: Kinematic and Liquefaction are open besides the free programs.': 'Plus plan: ücretsiz programlara ek olarak Kinematik ve Sıvılaşma açık.',
  'Limit Equilibrium, Bearing Capacity, Settlement and Soil Correlations': 'Limit Denge, Taşıma Gücü, Oturma ve Zemin Korelasyonları',
  'Unlimited analyses, results and CSV export': 'Sınırsız analiz, sonuçlar ve CSV dışa aktarma',
  'Everything in Plus': "Plus'taki her şey",
  'Rock Kinematics and Liquefaction': 'Kaya Kinematiği ve Sıvılaşma',
  'Pile Capacity and Pile Wall Analysis': 'Kazık Taşıma Gücü ve Kazıklı Perde Analizi',
  'Every program still to come but the finite element ones: rock mass, MSE walls, retaining walls': 'Sonlu eleman dışında gelecek tüm programlar: kaya kütlesi, MSE duvarları, istinat duvarları',
  'Tunnels': 'Tüneller',
  'Tunnel excavation and support: convergence–confinement and lining design.': 'Tünel kazısı ve desteği: yakınsama–sınırlama ve kaplama tasarımı.',
  'This program is part of the Plus plan.': 'Bu program Plus planına dahildir.',
  'This program is part of the Pro plan.': 'Bu program Pro planına dahildir.',
  'This account already has a paid plan; change it in the billing portal': 'Bu hesabın ücretli bir planı var; planı ödeme panelinden değiştirin',
  'Free with a verified .edu.tr e-mail address': '.edu.tr e-postasını doğrulayanlara ücretsiz',
  'Active on your account': 'Hesabınızda etkin',
  'Sign up with your .edu.tr address': '.edu.tr adresinizle kayıt olun',
  'For students and staff with a verified .edu.tr e-mail address': 'Doğrulanmış .edu.tr e-posta adresi olan öğrenci ve personel için',
  'Free for as long as the address is yours': 'Adres sizde olduğu sürece ücretsiz',
  'Team': 'Ekip',
  'Saving projects and Excel exports are Pro features.': 'Proje kaydetme ve Excel çıktıları Pro özellikleridir.',
  'Pro features need a Pro plan.': 'Bu özellik için Pro plan gerekir.',
}

const fill = (text, vars) => (vars
  ? text.replace(/\{(\w+)\}/g, (m, name) => (name in vars ? vars[name] : m)) : text)

/** Translate an English string; without an entry (or in English) it is returned unchanged. */
export const t = (text, vars) => fill(lang.value === 'tr' ? (TR[text] ?? text) : text, vars)

export default { install(app) { app.config.globalProperties.$t = t } }
